import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ShoppingCart, Menu, VideoOff } from 'lucide-react';
import { Alert, Btn, LoadingSpinner, MultilineText, NoticeCard } from '@/presentation/component/ui';
import { AudioPlayer } from '@/presentation/component/course/AudioPlayer';
import { VideoPlayer } from '@/presentation/component/course/VideoPlayer';
import { ChapterSidebar } from '@/presentation/component/course/ChapterSidebar';
import { CourseDocumentList } from '@/presentation/component/course/CourseDocumentList';
import { CoursePlayerNavbar } from '@/presentation/component/course/CoursePlayerNavbar';
import { useCourse } from '@/presentation/hook/useCourse';
import { useCourseProgress } from '@/presentation/hook/useCourseProgress';
import { useCourseProgressReporter } from '@/presentation/hook/useCourseProgressReporter';
import { useSubcategoryAccess } from '@/presentation/hook/useSubcategoryAccess';
import { hasCourseAccess } from '@/domain/model/accessUtils';
import { noticeIconClass } from '@/presentation/component/course/noticeStyles';
import { resumePercentage } from '@/domain/model/courseProgressUtils';
import type { Course } from '@/domain/model/Course';

const LoginRequiredNotice = ({ redirectPath }: { redirectPath: string }) => {
  const { t } = useTranslation();

  return (
    <NoticeCard
      icon={<Lock className={noticeIconClass} />}
      title={t('coursePlayer.loginRequired')}
      description={t('coursePlayer.loginRequiredDesc')}
    >
      <Link to={`/login?from=${encodeURIComponent(redirectPath)}`}>
        <Btn kind="primary">{t('buttons.signIn')}</Btn>
      </Link>
    </NoticeCard>
  );
};

const PurchaseRequiredNotice = ({ subcategoryId }: { subcategoryId: string }) => {
  const { t } = useTranslation();

  return (
    <NoticeCard
      icon={<Lock className={noticeIconClass} />}
      title={t('coursePlayer.purchaseRequired')}
      description={t('coursePlayer.purchaseRequiredDesc')}
    >
      <Link to={`/subcategory/${subcategoryId}`}>
        <Btn kind="primary" icon={<ShoppingCart className="w-4 h-4" />}>
          {t('buttons.viewPurchaseOptions')}
        </Btn>
      </Link>
    </NoticeCard>
  );
};

const MediaUnavailableNotice = () => {
  const { t } = useTranslation();

  return (
    <NoticeCard
      icon={<VideoOff className={noticeIconClass} />}
      title={t('coursePlayer.mediaUnavailable')}
      description={t('coursePlayer.mediaUnavailableDesc')}
    />
  );
};

export const CourseViewPage = () => {
  const { t } = useTranslation();
  const { courseId, subcategoryId } = useParams<{ courseId: string; subcategoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentCourse,
    currentSubcategory,
    isLoading,
    error,
    fetchCourseAndSubcategory,
    clearCourse,
  } = useCourse();
  const { progressByCourseId, progressStatus, progressError, fetchProgress, clearProgress } =
    useCourseProgress();
  const { hasAccess: hasSubcatAccess, isAuthenticated } = useSubcategoryAccess(subcategoryId);
  const hasAccess = hasCourseAccess(currentCourse, hasSubcatAccess);
  const isProgressTracked = isAuthenticated && hasAccess;
  const { reportProgress, reportCompleted } = useCourseProgressReporter({
    courseId,
    isEnabled: isProgressTracked && progressStatus === 'ready',
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (courseId && subcategoryId) {
      fetchCourseAndSubcategory(courseId, subcategoryId);
    }
    return () => clearCourse();
  }, [courseId, subcategoryId, fetchCourseAndSubcategory, clearCourse]);

  useEffect(() => () => clearProgress(), [subcategoryId, clearProgress]);

  const chapters = useMemo(() => currentSubcategory?.chapters || [], [currentSubcategory]);
  const chapterIdsKey = chapters.map((chapter) => chapter.id).join(',');

  useEffect(() => {
    if (!isProgressTracked || !chapterIdsKey) return;
    fetchProgress(chapterIdsKey.split(','));
  }, [isProgressTracked, chapterIdsKey, fetchProgress]);

  const allCourses = chapters.flatMap((chapter) => chapter.courses || []);
  const currentIndex = allCourses.findIndex((c) => c.id === currentCourse?.id);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < allCourses.length - 1;

  const handleCourseSelect = (course: Course) => {
    navigate(`/course/${subcategoryId}/${course.id}`);
  };

  const handlePreviousLesson = () => {
    if (hasPrevious) {
      navigate(`/course/${subcategoryId}/${allCourses[currentIndex - 1].id}`);
    }
  };

  const handleNextLesson = () => {
    reportCompleted();
    if (hasNext) {
      navigate(`/course/${subcategoryId}/${allCourses[currentIndex + 1].id}`);
    }
  };

  const handleMediaComplete = () => {
    reportCompleted();
    setTimeout(() => {
      if (hasNext) {
        navigate(`/course/${subcategoryId}/${allCourses[currentIndex + 1].id}`);
      }
    }, 1000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center max-w-md">
          <Alert kind="bad" className="mb-4">
            {t(error)}
          </Alert>
          <Link to="/formation">
            <Btn kind="primary">{t('buttons.backToFormations')}</Btn>
          </Link>
        </div>
      </div>
    );
  }

  if (!currentCourse || !currentSubcategory) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--color-ink)] mb-4">
            {t('category.formationNotFound')}
          </h1>
          <Link to="/formation">
            <Btn kind="primary">{t('buttons.backToFormations')}</Btn>
          </Link>
        </div>
      </div>
    );
  }

  const renderPlayerArea = () => {
    if (!hasAccess && !isAuthenticated) {
      return <LoginRequiredNotice redirectPath={location.pathname} />;
    }
    if (!hasAccess) {
      return <PurchaseRequiredNotice subcategoryId={currentSubcategory.id} />;
    }
    if (!currentCourse.mediaUrl) {
      return <MediaUnavailableNotice />;
    }
    if (isProgressTracked && (progressStatus === 'idle' || progressStatus === 'loading')) {
      return (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    const initialResumePercent = resumePercentage(progressByCourseId[currentCourse.id]);

    if (currentCourse.mediaType === 'AUDIO') {
      return (
        <AudioPlayer
          key={currentCourse.id}
          audioUrl={currentCourse.mediaUrl}
          title={currentCourse.title}
          initialResumePercent={initialResumePercent}
          onProgress={reportProgress}
          onComplete={handleMediaComplete}
        />
      );
    }
    return (
      <VideoPlayer
        key={currentCourse.id}
        videoUrl={currentCourse.mediaUrl}
        initialResumePercent={initialResumePercent}
        onProgress={reportProgress}
        onComplete={handleMediaComplete}
      />
    );
  };

  return (
    <div className="h-screen flex flex-col bg-[var(--color-bg)]">
      <CoursePlayerNavbar
        subcategoryId={currentSubcategory.id}
        subcategoryName={currentSubcategory.name}
        courseTitle={currentCourse.title}
        onPreviousLesson={handlePreviousLesson}
        onNextLesson={handleNextLesson}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <button
          className="lg:hidden flex items-center gap-2 p-3 text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] border-b border-[var(--color-line)]"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <Menu className="w-5 h-5" />
          <span className="text-sm font-medium">{t('coursePlayer.courseContent')}</span>
        </button>

        <div
          className={`${sidebarOpen ? 'block' : 'hidden'} lg:block lg:w-80 shrink-0 overflow-hidden border-r border-[var(--color-line)]`}
        >
          <ChapterSidebar
            subcategoryName={currentSubcategory.name}
            subcategoryId={currentSubcategory.id}
            subcategoryPrice={currentSubcategory.price}
            chapters={chapters}
            currentCourse={currentCourse}
            onCourseSelect={handleCourseSelect}
            progressByCourseId={progressByCourseId}
            hasAccess={hasSubcatAccess}
          />
        </div>

        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="flex-1 p-4 lg:p-8 flex items-start justify-center">
            <div className="w-full max-w-4xl">
              {renderPlayerArea()}

              {progressError ? (
                <Alert kind="warn" className="mt-4">
                  {t(progressError)}
                </Alert>
              ) : null}

              <div className="mt-6">
                <h2 className="text-2xl font-bold text-[var(--color-ink)] mb-4">
                  {currentCourse.title}
                </h2>
                <MultilineText className="text-[var(--color-ink-soft)] leading-relaxed">
                  {currentCourse.description}
                </MultilineText>
              </div>

              <CourseDocumentList documents={currentCourse.documents} hasAccess={hasAccess} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
